import React, {InputHTMLAttributes, useRef} from "react";
import {adminApi, axiosUtil} from "@utils";
import Button from ".";
import styles from "./style.module.scss";
import {useSetRecoilState} from "recoil";
import {adminStates} from "@recoils";
import Resizer from "react-image-file-resizer";

interface IProps extends InputHTMLAttributes<HTMLInputElement> {
  height?: number;
  text?: string;
  fileUpload?: ({
    files,
    thumbList,
  }: {
    files: string[];
    thumbList?: string[];
  }) => void;
  callback?: () => void;
  uploadUrl?: string;
  type?: "image" | "excel";
  backgroundColor?: string;
  fontColor?: string;
  isThumbnail?: boolean;
}

const resizeFile = ({
  file,
  maxWidth = 1000,
  maxHeight = 1000,
  format = "JPEG",
  quality = 100,
  rotate = 0,
  outputType = "file",
}: {
  file: File;
  maxWidth?: number;
  maxHeight?: number;
  format?: "JPEG" | "PNG" | "WEBP";
  quality?: number;
  rotate?: number;
  outputType?: "file" | "base64" | "blob";
}) => {
  return new Promise(res => {
    Resizer.imageFileResizer(
      file,
      maxWidth,
      maxHeight,
      format,
      quality,
      rotate,
      uri => res(uri), // responseUriFunc
      outputType,
    );
  });
};

export default function FileUploadButton({
  text,
  fileUpload,
  callback,
  uploadUrl,
  height,
  type = "image",
  backgroundColor,
  fontColor,
  isThumbnail = false,
  ...rest
}: IProps) {
  const setAlertModal = useSetRecoilState(adminStates.alertState);
  const setLoading = useSetRecoilState(adminStates.loadingState);
  const ref = useRef<HTMLInputElement>(null);

  function fileSelect() {
    if (ref.current) {
      ref.current.files = null;
    }
    ref.current?.click();
  }

  async function uploadFile(event: InputHTMLAttributes<HTMLInputElement>) {
    try {
      if (!event) {
        throw Error("event is null");
      }
      if (!uploadUrl) {
        throw Error("uploadUrl is null");
      }

      const files = ref.current?.files;
      if (!files || files.length === 0) {
        throw Error("files is null");
      }

      setLoading(true);
      if (type === "image") {
        const fileList: File[] = [];
        const length = files.length;
        for (let i = 0; i < length; i++) {
          let file = files[i];
          if (file.size > 2 * 1024 * 1024) {
            // 2MB 이상 리사이징
            file = (await resizeFile({file: files[i]})) as File;
          }
          fileList.push(file);
        }

        const data = await axiosUtil.imageUpload({
          files: fileList,
          endPoint: uploadUrl,
        });
        if (!data) {
          throw Error("사진업로드 실패");
        }
        const {imageList, thumbList} = data;

        if (!imageList || imageList?.length === 0) {
          throw Error("사진업로드 실패");
        }

        fileUpload?.({files: imageList, thumbList});
      } else if (type === "excel") {
        const {message} = await axiosUtil.fileUpload({
          files,
          endPoint: uploadUrl,
        });
        setAlertModal({isVisible: true, title: message});
      }
      ref.current.files = null;
      ref.current.value = "";
      callback?.();
      setLoading(false);
    } catch (error) {
      console.log("FileUploadButton uploadFile error", error);
      setLoading(false);
    }
  }

  return (
    <div className={styles.inputLabel} onClick={fileSelect}>
      <Button
        text={text}
        fontSize={12}
        fontWeight={500}
        height={height}
        backgroundColor={backgroundColor}
        fontColor={fontColor}
      />
      <input
        {...rest}
        ref={ref}
        type="file"
        onChange={uploadFile}
        accept={
          type === "image"
            ? "image/*"
            : type === "excel"
              ? "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              : "*"
        }
      />
    </div>
  );
}
